import { useEffect, useRef, useState } from "react"
import { X } from "lucide-react"

export interface Mentionable {
  id: string
  name: string
  email?: string
  avatar?: string
}

interface MentionAutocompleteProps {
  textareaRef: React.RefObject<HTMLTextAreaElement>
  mentionables: Mentionable[]
  onMentionSelect: (mention: Mentionable, startPos: number, endPos: number) => void
  value: string
}

interface MentionState {
  isOpen: boolean
  searchText: string
  startPos: number
  selectedIndex: number
  filtered: Mentionable[]
}

export function MentionAutocomplete({
  textareaRef,
  mentionables,
  onMentionSelect,
  value,
}: MentionAutocompleteProps) {
  const [mention, setMention] = useState<MentionState>({
    isOpen: false,
    searchText: "",
    startPos: 0,
    selectedIndex: 0,
    filtered: [],
  })
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Detect @ mention trigger
  useEffect(() => {
    const handleInputChange = () => {
      if (!textareaRef.current) return

      const text = textareaRef.current.value
      const cursorPos = textareaRef.current.selectionStart

      // Find the last @ before cursor
      const textBeforeCursor = text.substring(0, cursorPos)
      const lastAtIndex = textBeforeCursor.lastIndexOf("@")

      if (lastAtIndex === -1) {
        setMention(prev => ({ ...prev, isOpen: false }))
        return
      }

      // Check if @ is at word boundary (start of string or after space)
      if (lastAtIndex > 0 && textBeforeCursor[lastAtIndex - 1] !== " " && textBeforeCursor[lastAtIndex - 1] !== "\n") {
        setMention(prev => ({ ...prev, isOpen: false }))
        return
      }

      // Get search text after @
      const searchText = textBeforeCursor.substring(lastAtIndex + 1)

      // If search contains space, close dropdown
      if (searchText.includes(" ")) {
        setMention(prev => ({ ...prev, isOpen: false }))
        return
      }

      // Filter mentionables
      const filtered = mentionables.filter(
        m => m.name.toLowerCase().includes(searchText.toLowerCase()) ||
             (m.email?.toLowerCase().includes(searchText.toLowerCase()) ?? false)
      )

      setMention({
        isOpen: filtered.length > 0,
        searchText,
        startPos: lastAtIndex,
        selectedIndex: 0,
        filtered,
      })
    }

    const textarea = textareaRef.current
    if (!textarea) return

    textarea.addEventListener("input", handleInputChange)
    textarea.addEventListener("click", handleInputChange)
    return () => {
      textarea.removeEventListener("input", handleInputChange)
      textarea.removeEventListener("click", handleInputChange)
    }
  }, [textareaRef, mentionables])

  // Handle keyboard navigation
  useEffect(() => {
    if (!mention.isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setMention(prev => ({
          ...prev,
          selectedIndex: (prev.selectedIndex + 1) % prev.filtered.length,
        }))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setMention(prev => ({
          ...prev,
          selectedIndex: (prev.selectedIndex - 1 + prev.filtered.length) % prev.filtered.length,
        }))
      } else if (e.key === "Enter") {
        e.preventDefault()
        const selected = mention.filtered[mention.selectedIndex]
        if (selected) {
          handleSelectMention(selected)
        }
      } else if (e.key === "Escape") {
        setMention(prev => ({ ...prev, isOpen: false }))
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [mention])

  const handleSelectMention = (mentionable: Mentionable) => {
    if (!textareaRef.current) return

    const text = textareaRef.current.value
    const startPos = mention.startPos
    const endPos = textareaRef.current.selectionStart

    onMentionSelect(mentionable, startPos, endPos)
    setMention(prev => ({ ...prev, isOpen: false }))
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        if (textareaRef.current && !textareaRef.current.contains(e.target as Node)) {
          setMention(prev => ({ ...prev, isOpen: false }))
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [textareaRef])

  if (!mention.isOpen || mention.filtered.length === 0) {
    return null
  }

  return (
    <div
      ref={dropdownRef}
      className="absolute bottom-full left-0 w-64 bg-white border border-[#ece9f5] rounded-lg shadow-lg z-50 mb-2"
    >
      <div className="max-h-48 overflow-y-auto">
        {mention.filtered.map((m, idx) => (
          <button
            key={m.id}
            onClick={() => handleSelectMention(m)}
            className={`w-full px-3 py-2.5 text-left flex items-center gap-3 text-[13px] transition-colors ${
              idx === mention.selectedIndex
                ? "bg-[#f5f3ff] text-[#1a1530]"
                : "text-[#6b6386] hover:bg-[#f5f3ff]"
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-[#7c3aed] flex items-center justify-center text-white text-[11px] font-semibold flex-shrink-0">
              {m.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{m.name}</div>
              {m.email && <div className="text-[11px] text-[#8b84a3] truncate">{m.email}</div>}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// Component to display mentions as styled tags in text
interface MentionTagProps {
  mention: Mentionable
  onRemove: () => void
}

export function MentionTag({ mention, onRemove }: MentionTagProps) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#ede9fe] border border-[#ddd6fe] rounded-full text-[12px] font-medium text-[#7c3aed] mr-1 mb-1">
      @{mention.name}
      <button
        onClick={onRemove}
        className="text-[#8b5cf6] hover:text-[#7c3aed] ml-0.5"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}
